import React, { useState, useEffect } from 'react';
import { getSupabase } from '../../lib/supabase-lazy';
import { useAuth } from '../../contexts/AuthContextLazy';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';
import { 
  BellIcon,
  ChatBubbleLeftIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  CalendarIcon,
  CheckCircleIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline';

interface Notification {
  id: string;
  recipient_id: string;
  recipient_type: string;
  thread_id: string;
  notification_type: string;
  title: string;
  message: string;
  action_url: string;
  is_read: boolean;
  created_at: string;
}

export default function Notifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | 'archive'>('all');
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);

  useEffect(() => {
    fetchNotifications();
  }, [user, filter]);

  const fetchNotifications = async () => {
    try {
      const supabase = await getSupabase();
      
      let query = supabase
        .from('client_notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter === 'unread') {
        query = query.eq('is_read', false);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      setNotifications(data || []);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching notifications:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationIds: string[]) => {
    try {
      const supabase = await getSupabase();
      
      const { error } = await supabase
        .from('client_notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .in('id', notificationIds);

      if (error) throw error;
      
      setNotifications(prev =>
        prev.map(n => notificationIds.includes(n.id) ? { ...n, is_read: true } : n)
      );
      setSelectedNotifications([]);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error marking notifications as read:', error);
      }
    }
  };

  const deleteNotifications = async (notificationIds: string[]) => {
    try {
      const supabase = await getSupabase();
      
      const { error } = await supabase
        .from('client_notifications')
        .delete()
        .in('id', notificationIds);

      if (error) throw error;
      
      setNotifications(prev => prev.filter(n => !notificationIds.includes(n.id)));
      setSelectedNotifications([]);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error deleting notifications:', error);
      }
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_message':
        return <ChatBubbleLeftIcon className="h-5 w-5" />;
      case 'document_uploaded':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'deadline_reminder':
        return <CalendarIcon className="h-5 w-5" />;
      case 'status_change':
        return <ExclamationCircleIcon className="h-5 w-5" />;
      default:
        return <BellIcon className="h-5 w-5" />;
    }
  };

  const groupNotificationsByDate = (notifications: Notification[]) => {
    const groups: { [key: string]: Notification[] } = {};
    
    notifications.forEach(notification => {
      const date = new Date(notification.created_at);
      let dateKey: string;
      
      if (isToday(date)) {
        dateKey = 'Today';
      } else if (isYesterday(date)) {
        dateKey = 'Yesterday';
      } else {
        dateKey = format(date, 'MMMM d, yyyy');
      }
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(notification);
    });
    
    return groups;
  };

  const groupedNotifications = groupNotificationsByDate(notifications);
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header Actions */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
          </p>
        </div>
        
        {selectedNotifications.length > 0 && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => markAsRead(selectedNotifications)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Mark as read
            </button>
            <button
              onClick={() => deleteNotifications(selectedNotifications)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <ArchiveBoxIcon className="h-4 w-4 mr-2" />
              Archive
            </button>
          </div>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['all', 'unread'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm capitalize
                ${filter === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab} {tab === 'unread' && unreadCount > 0 && `(${unreadCount})`}
            </button>
          ))}
        </nav>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-500 mt-4">Loading notifications...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BellIcon className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
          </h3>
          <p className="text-sm text-gray-500">
            {filter === 'unread' 
              ? 'All caught up! You\'ve read all your notifications.'
              : 'You\'ll see notifications here when there\'s activity'}
          </p>
        </div>
      ) : (
        <div className="bg-white portal-content-shadow rounded-lg overflow-hidden">
          {Object.entries(groupedNotifications).map(([dateKey, dateNotifications]) => (
            <div key={dateKey}>
              <div className="px-6 py-3 bg-gray-50 border-y border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{dateKey}</p>
              </div>
              <div className="divide-y divide-gray-200">
                {dateNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`
                      relative px-6 py-4 hover:bg-gray-50 transition-all duration-150
                      ${!notification.is_read ? 'bg-blue-50 border-l-4 border-blue-400' : ''}
                    `}
                  >
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        checked={selectedNotifications.includes(notification.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedNotifications([...selectedNotifications, notification.id]);
                          } else {
                            setSelectedNotifications(selectedNotifications.filter(id => id !== notification.id));
                          }
                        }}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      
                      <Link
                        to={notification.action_url}
                        onClick={() => markAsRead([notification.id])}
                        className="flex-1 flex items-start gap-3"
                      >
                        <div className={`p-2.5 rounded-lg flex-shrink-0 ${
                          !notification.is_read ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {getNotificationIcon(notification.notification_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${
                            !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-0.5">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" />
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                          </div>
                        )}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}